(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-polyphenol-compounds';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DRAWABLE = 'path, line, polyline, polygon, circle, ellipse, rect';

  /* ---- SVG "draw itself" animation ------------------------------------- */
  function nextFrame(fn) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(fn);
    });
  }

  // Does the element paint a stroke we can trace with a dash animation?
  function hasVisibleStroke(el) {
    var cs = window.getComputedStyle(el);
    var stroke = cs.stroke;
    var width = parseFloat(cs.strokeWidth) || 0;
    return (
      width > 0 &&
      stroke &&
      stroke !== 'none' &&
      stroke !== 'rgba(0, 0, 0, 0)' &&
      stroke !== 'transparent'
    );
  }

  var EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

  function strokeDraw(items) {
    var stagger = 110; // ms between lines
    var duration = 1500; // ms per line
    items.forEach(function (item, idx) {
      var el = item.el;
      el.style.strokeDasharray = String(item.len);
      el.style.strokeDashoffset = String(item.len);
      el.getBoundingClientRect(); // lock in the hidden start state
      el.style.transition = 'stroke-dashoffset ' + duration + 'ms ' + EASE + ' ' + idx * stagger + 'ms';
    });
    nextFrame(function () {
      items.forEach(function (item) {
        item.el.style.strokeDashoffset = '0';
      });
    });
  }

  // Fallback for fill-based SVGs (no traceable strokes): wipe it in L→R so it
  // still reads as being "drawn".
  function wipeReveal(svg) {
    var from = 'inset(0 100% 0 0)';
    var to = 'inset(0 0 0 0)';
    svg.style.webkitClipPath = from;
    svg.style.clipPath = from;
    svg.getBoundingClientRect();
    svg.style.transition = 'clip-path 1900ms ' + EASE + ', -webkit-clip-path 1900ms ' + EASE;
    nextFrame(function () {
      svg.style.webkitClipPath = to;
      svg.style.clipPath = to;
    });
  }

  function drawSvg(container) {
    if (!container || container.dataset.drawn) return;
    var svg = container.querySelector('svg');
    if (!svg) return;
    container.dataset.drawn = '1';

    // Reduced motion: reveal instantly.
    if (reduceMotion) return;

    var els = svg.querySelectorAll(DRAWABLE);
    var strokeItems = [];
    for (var i = 0; i < els.length; i++) {
      if (!hasVisibleStroke(els[i])) continue;
      var len = 0;
      try {
        if (typeof els[i].getTotalLength === 'function') len = els[i].getTotalLength();
      } catch (e) {
        len = 0;
      }
      if (len > 0.5) strokeItems.push({ el: els[i], len: len });
    }

    if (strokeItems.length) {
      strokeDraw(strokeItems);
    } else {
      wipeReveal(svg);
    }
  }

  // Clear a previous run so the SVG can be drawn again (used on tab clicks).
  function replaySvg(container) {
    if (!container) return;
    var svg = container.querySelector('svg');
    if (!svg) return;

    delete container.dataset.drawn;
    svg.style.transition = '';
    svg.style.clipPath = '';
    svg.style.webkitClipPath = '';
    var els = svg.querySelectorAll(DRAWABLE);
    for (var i = 0; i < els.length; i++) {
      els[i].style.transition = '';
      els[i].style.strokeDasharray = '';
      els[i].style.strokeDashoffset = '';
    }
    svg.getBoundingClientRect(); // flush the reset before re-animating
    drawSvg(container);
  }

  function observeSvgs(section) {
    var containers = section.querySelectorAll('[data-draw-svg]');
    if (!containers.length) return null;

    if (!('IntersectionObserver' in window)) {
      containers.forEach(drawSvg);
      return null;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            drawSvg(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );
    containers.forEach(function (c) {
      io.observe(c);
    });
    return io;
  }

  /* ---- Tabs (pills switch the right-hand panel) ------------------------ */
  function initSection(section) {
    var pills = section.querySelectorAll('.oddit-polyphenol-compounds__pill');
    var panels = section.querySelectorAll('.oddit-polyphenol-compounds__panel');
    if (!pills.length || !panels.length) return;

    observeSvgs(section);

    function activate(index) {
      for (var i = 0; i < pills.length; i++) {
        var active = i === index;
        pills[i].classList.toggle('oddit-polyphenol-compounds__pill--active', active);
        pills[i].setAttribute('aria-selected', active ? 'true' : 'false');
        pills[i].setAttribute('tabindex', active ? '0' : '-1');
      }
      for (var j = 0; j < panels.length; j++) {
        var panelActive = j === index;
        panels[j].classList.toggle('oddit-polyphenol-compounds__panel--active', panelActive);
        if (panelActive) {
          panels[j].removeAttribute('hidden');
          // The panel is now visible — replay its SVG draw each time it opens.
          // Defer a frame so the just-shown element can be measured.
          (function (panel) {
            window.requestAnimationFrame(function () {
              replaySvg(panel.querySelector('[data-draw-svg]'));
            });
          })(panels[j]);
        } else {
          panels[j].setAttribute('hidden', '');
        }
      }
    }

    function onKeydown(e) {
      var current = Number(e.currentTarget.getAttribute('data-tab-index'));
      if (isNaN(current)) return;
      var next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        next = (current + 1) % pills.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        next = (current - 1 + pills.length) % pills.length;
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = pills.length - 1;
      }
      if (next !== null) {
        e.preventDefault();
        activate(next);
        pills[next].focus();
      }
    }

    for (var k = 0; k < pills.length; k++) {
      pills[k].addEventListener('click', function (e) {
        var index = Number(e.currentTarget.getAttribute('data-tab-index'));
        if (!isNaN(index)) activate(index);
      });
      pills[k].addEventListener('keydown', onKeydown);
    }
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });
})();
